import React, { useEffect, useState, useContext } from 'react';
import { Card, Flex } from '../../design-system';
import { Container, Text, Box, Button } from '../../design-system';
import styled, { css } from 'styled-components';
import {
  Link,
  useNavigate,
  useLocation,
  Routes,
  Route,
} from 'react-router';
import { useEffectOnce, useToggle } from 'react-use';
import axios from 'axios';
import AgencyList from './Agency/agencyList';
import PermissionRoute from '../../routes/route';
import UserList from './Users/UserList';
import {
  ORG_MEMBERS_COLUMNS,
  ADD_MEMBER_MODAL_FIELDS,
} from '../Account/constants';
import ModalForm from '../../components/ModalForm';
import { Table, TableCardHeader } from '../../components/Table';
import { SUCCESS_MSG, ERR_MSG } from '../../globalConstants';
import Alert from '../../helpers/alert';
import { useData } from '../../helpers/data-loader';
import { ACTIONS, Session } from '../../contexts/sessionContext';
import {
  errorMsg,
  catchPlanDetailError,
  required,
  createSelectOptions,
  selectionRequired,
  atLeastOne,
} from '../../helpers/utils';

const Tab = styled(Link)<{ selected?: boolean }>(
  ({ theme, selected }) => css`
    padding: 10px;
    height: 100%;
    color: ${selected ? theme.colors.primary : theme.colors.secondary};
    border-bottom: 2px solid transparent;
    border-color: ${selected ? theme.colors.primary : 'transparent'};
    font-size: 1.1rem;
    text-decoration: none;
  `,
);

const HeaderText = styled(Text)(
  ({ theme }) => css`
    white-space: nowrap;
    border-right: 1px solid ${theme.colors.gray};
  `,
);

const TABS = [
  {
    id: 0,
    title: 'Agencies',
    component: AgencyList,
    path: '/administration/agencies',
  },
  {
    id: 1,
    title: 'Users',
    component: UserList,
    path: '/administration/users',
  },
];

const findTab = (haystack, needle) =>
  haystack.find((tab) => needle.includes(tab.path) && tab);

const Subscription = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const currentTab = findTab(TABS, pathname);
  const [selectedTab, setSelectedTab] = useState<any>(currentTab);
  const {
    state: { user },
    dispatch,
  } = useContext(Session);

  const [isAddModalOpen, toggleAddModal] = useToggle(false);

  const [clients, fetchClients] = useData('/client', {
    errorMsg: errorMsg('clients'),
  });

  const clientChoices = createSelectOptions(clients).sort((a, b) => {
    const labelA = a.label.toUpperCase();
    const labelB = b.label.toUpperCase();
    return labelA < labelB ? -1 : 1;
  });

  const [agencies, fetchAgencies, loadingAgencies] = useData('/admin', {
    errorMsg: errorMsg('Agencies'),
  });

  const agencyChoices = createSelectOptions(agencies).sort((a, b) => {
    const labelA = a.label.toUpperCase();
    const labelB = b.label.toUpperCase();
    return labelA < labelB ? -1 : 1;
  });

  useEffect(() => {
    fetchAgencies();
  }, [fetchAgencies]);

  useEffect(() => {
    if (!selectedTab || !findTab(TABS, pathname)) {
      setSelectedTab(TABS[0]);
      navigate(TABS[0].path, { replace: true });
    }
    if (selectedTab && !pathname.includes(selectedTab.path)) {
      setSelectedTab(findTab(TABS, pathname));
    }
    // Only redirect when the resolved tab path actually differs from the
    // current path — navigating to the same path in a loop otherwise.
    const resolved = findTab(TABS, pathname);
    if (resolved && resolved.path !== pathname) {
      navigate(resolved.path, { replace: true });
    }
  }, [selectedTab, pathname, navigate]);

  useEffectOnce(() => {
    fetchClients();
  });

  const handleAddUser = async (values: any) => {
    values.clients = [];

    const isClient = values.isClient === 'external';

    try {
      const response = await axios.post('/invite', { ...values, isClient });
      const { data } = response;
      Alert.success(SUCCESS_MSG);
      dispatch({
        type: ACTIONS.SET_USER,
        value: { ...user, invites: [...user.invites, data] },
      });
      toggleAddModal(false);
    } catch (error) {
      Alert.error(error.response?.data?.error || ERR_MSG);
    }
  };

  const extraFields = [
    {
      name: 'role_id',
      label: 'Role',
      component: 'select',
      options:
        // values start at 3 to correspond
        // to the equivalent role.id in DB
        [
          {
            value: 3,
            label: 'Editor',
          },
          {
            value: 4,
            label: 'Viewer',
          },
        ],
      validate: required,
    },
    {
      name: 'agency_id',
      label: 'Agency',
      component: 'select',
      options: agencyChoices,
      validate: selectionRequired,
    },
    {
      name: 'isClient',
      label: 'External User',
      component: 'checkbox',
    },
    {
      name: 'clients',
      label: 'Clients',
      component: 'checkbox-group',
      choices: clientChoices,
    },
  ];

  if (selectedTab) {
    return (
      <Container gutter={50} fluid>
        <Card mt={30}>
          <Flex alignItems="center">
            <HeaderText fontSize="1.8rem" fontWeight={600} pr={3}>
              Super Admin
            </HeaderText>
            <Flex width="100%" ml={3}>
              {TABS.map(({ id, title, path }) => (
                <Tab to={path} selected={id === selectedTab.id} key={title}>
                  {title}
                </Tab>
              ))}
            </Flex>
            <Box justifySelf="flex-end" ml="auto">
              <Button
                variant="primaryText"
                type="button"
                onClick={toggleAddModal}
                leftIcon="plus"
                leftIconProps={{ size: 20 }}
              >
                Invite New User
              </Button>
            </Box>
          </Flex>
          <Routes>
            <Route
              path={selectedTab.path.replace('/administration/', '')}
              element={<PermissionRoute component={selectedTab.component} />}
            />
          </Routes>
          <ModalForm
            title="Add New User"
            fields={[...ADD_MEMBER_MODAL_FIELDS, ...extraFields]}
            isOpen={isAddModalOpen}
            onClose={() => toggleAddModal(false)}
            onSubmit={handleAddUser}
            initialValues={{ setPassword: 'send' }}
            oneColumn
          />
        </Card>
      </Container>
    );
  }
  return null;
};

export default Subscription;
